import axios from 'axios';
import * as Moment from 'moment-timezone';
import moment from 'moment-timezone';
import queryString from 'query-string';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components';
import BookingAPI from '../../api/booking.api';
import { bugsnagNotify } from '../../bugsnag';
import Footer from '../../components/Footer';
import HeaderOLD from '../../components/OLD_Header';
import LoadingPage from '../../components/LoadingPage';
import { useMediaQuery } from '@material-ui/core';
import { trackDineoutDiscovery } from '@dineouticeland/do-tracking';
import { useCompanyIdentifier } from '../../contexts/TrackingContext';
import { BookingStatusType } from '../../enums/BookingStatusType.ts';
import URLBuilder from '../../utils/URLBuilder.ts';
import { normaliseHTMl } from '../TA/utils/itemUtils.ts';

const BG = '#f7f8fa';
const CARD = '#ffffff';
const INK = '#1c1c1c';
const MUTED = '#9b9b9b';
const LINE = '#e9eef3';
const ACCENT = '#ff6e3d';
const OK = '#1f9d63';

const Style = styled.div`
    background-color: ${BG};
    color: ${INK};

    .h-header {
        padding: 16px;
        background-color: rgba(247, 248, 250, 0.72);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        border-bottom: 1px solid rgba(28, 28, 28, 0.05);
    }

    .conf-wrap {
        max-width: 768px;
        margin: 0 auto;
        padding: 120px 24px 72px;

        @media screen and (max-width: 620px) {
            padding: 88px 20px 56px;
        }
    }

    /* ---------- Status ---------- */
    .conf-status {
        text-align: center;
        margin-bottom: 32px;
    }

    .conf-check {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: ${OK};
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 16px;
        box-shadow: 0 10px 24px -8px rgba(31, 157, 99, 0.5);
    }

    .conf-check .material-icons {
        color: #fff;
        font-size: 32px;
    }

    .conf-status h1 {
        font-size: 30px;
        font-weight: 600;
        letter-spacing: -0.02em;
        margin: 0;
        text-wrap: balance;
    }

    .conf-code {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-top: 14px;
        font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
        font-size: 13px;
        font-weight: 700;
        background: ${CARD};
        border: 1px dashed ${LINE};
        color: ${INK};
        border-radius: 8px;
        padding: 8px 13px;
    }

    /* ---------- Ticket ---------- */
    .conf-ticket {
        position: relative;
        background: ${CARD};
        border: 1px solid ${LINE};
        border-radius: 18px;
        box-shadow: 0 24px 48px -30px rgba(20, 15, 8, 0.35);
        overflow: hidden;
    }

    .conf-ticket-top {
        padding: 28px 32px;
        display: flex;
        align-items: center;
        gap: 18px;
    }

    .conf-logo {
        width: 64px;
        height: 64px;
        border-radius: 16px;
        flex-shrink: 0;
        background-color: ${BG};
        background-size: cover;
        background-position: center center;
    }

    .conf-ticket-top h2 {
        font-size: 23px;
        font-weight: 600;
        letter-spacing: -0.01em;
        margin: 0;
    }

    .conf-ticket-top .addr {
        color: ${MUTED};
        font-size: 13.5px;
        margin-top: 3px;
    }

    .conf-perf {
        position: relative;
        height: 1px;
        margin: 0 24px;
        border-top: 2px dashed ${LINE};
    }

    .conf-perf::before,
    .conf-perf::after {
        content: '';
        position: absolute;
        top: 50%;
        width: 26px;
        height: 26px;
        background: ${BG};
        border: 1px solid ${LINE};
        border-radius: 50%;
        transform: translateY(-50%);
    }

    .conf-perf::before {
        left: -37px;
    }

    .conf-perf::after {
        right: -37px;
    }

    .conf-stub {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
    }

    .conf-stub > div {
        padding: 24px 32px;
    }

    .conf-stub > div + div {
        border-left: 1px solid ${LINE};
    }

    .conf-stub .k {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: ${MUTED};
        margin-bottom: 7px;
    }

    .conf-stub .v {
        font-size: 22px;
        font-weight: 600;
        letter-spacing: -0.01em;
        font-variant-numeric: tabular-nums;

        @media screen and (max-width: 620px) {
            font-size: 18px;
        }
    }

    /* ---------- Actions ---------- */
    .conf-actions {
        margin-top: 24px;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 14px;

        @media screen and (max-width: 620px) {
            grid-template-columns: 1fr;
        }
    }

    .conf-link {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 9px;
        width: 100%;
        box-sizing: border-box;
        text-decoration: none;
        font-weight: 600;
        font-size: 15px;
        padding: 16px 18px;
        border-radius: 12px;
        border: 1px solid ${LINE};
        background: ${CARD};
        color: ${INK};
        transition: border-color 0.18s, transform 0.12s, box-shadow 0.18s;

        &:hover {
            border-color: ${ACCENT};
            transform: translateY(-1px);
        }
    }

    .conf-link .material-icons {
        font-size: 19px;
        color: ${ACCENT};
    }

    /* ---------- Note from restaurant ---------- */
    .conf-note {
        margin-top: 24px;
        background: ${CARD};
        border: 1px solid ${LINE};
        border-radius: 16px;
        padding: 24px 28px;
    }

    .conf-note h3 {
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: ${ACCENT};
        margin: 0 0 10px 0;
    }

    .conf-note .rest-text {
        font-size: 14.5px;
        line-height: 1.6;
        color: #4b463d;
        word-break: break-word;

        > p {
            margin: 0px;
            white-space: pre-wrap;
        }
    }

    /* ---------- Mini map ---------- */
    .conf-map {
        margin-top: 24px;
        height: 220px;
        border-radius: 16px;
        border: 1px solid ${LINE};
        background-color: #eef0ec;
        background-repeat: no-repeat;
        background-size: cover;
        background-position: center center;
    }
`;

const bookingApi = new BookingAPI();

type IProps = RouteComponentProps<any>;

const Confirmation: FunctionComponent<IProps> = ({ history }) => {
    const { t, i18n } = useTranslation();

    const params: any = queryString.parse(location.search);
    const isMobile = useMediaQuery('(max-width:768px)');

    const isWaitingListOnly =
        params.isWaitingList && params.isWaitingList.toLowerCase() === 'true';

    useEffect(() => {
        trackDineoutDiscovery('Reservation Step Viewed', { step: 'confirmation' });
    }, []);

    const [info, setInfo] = useState<{
        bookingId: number;
        start: Moment.Moment;
        end: Moment.Moment;
        guests: number;
        status: BookingStatusType;
        redirectUrl: string | null;
        restaurantId: number;
        restaurantName: string;
        restaurantAddress: string;
        restaurantWebsite: string;
        restaurantNote: string;
        restaurantSlug: string;
        restaurantLon: number;
        restaurantLat: number;
        logoUrl: string;
        gaTrackingId: string | null;
        fbPixelId: string | null;
        adImage: { imageUrl: string; href: string } | null;
    } | null>(null);

    // Set company identifier for restaurant-level tracking (cleared on unmount).
    useCompanyIdentifier(info?.restaurantId);

    useEffect(() => {
        const cancelToken = axios.CancelToken.source();

        bookingApi
            .getBookingInfoForConfirmation(params.guid, cancelToken)
            .then((res) => {
                setInfo({
                    ...res.data,
                    start: moment(res.data.start, moment.defaultFormatUtc),
                    end: moment(res.data.end, moment.defaultFormatUtc),
                });
            })
            .catch((e) => {
                if (!axios.isCancel(e)) {
                    bugsnagNotify(e);
                    throw new Error('Error fetching booking for confirmation.');
                }
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    useEffect(() => {
        if (info && !!info.redirectUrl && params.shouldRedirect !== 'false') {
            setTimeout(() => {
                params.shouldRedirect = false;
                history.replace(
                    history.location.pathname +
                        '?' +
                        queryString.stringify(params),
                );
                window.location.href = info.redirectUrl!;
            }, 3000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [info]);

    const showBadgeImageUrl = !!info?.adImage;

    if (!info) {
        return <LoadingPage />;
    }

    if (info.status == BookingStatusType.Hold) {
        const url = new URLBuilder(history);
        url.updateUrl(
            '/book/reservation-unsuccessful',
            {
                restaurantId: info.restaurantId!,
                restaurantSlug: info.restaurantSlug,
            },
            'Replace',
        );
    }

    return (
        <>
            <Style>
                <HeaderOLD inverted />

                <div className="conf-wrap">
                    <div className="conf-status">
                        <div className="conf-check">
                            <i className="material-icons">check</i>
                        </div>
                        <h1>
                            {t('pages.confirmation.thanks')}{' '}
                            {isWaitingListOnly
                                ? t('pages.confirmation.youHaveBeenAdded')
                                : t('pages.confirmation.yourBookingIsConfirmed')}
                        </h1>
                        <div className="conf-code">
                            {t('pages.confirmation.confirmationNumber')}
                            {info.bookingId}
                        </div>
                    </div>

                    <div className="conf-ticket">
                        <div className="conf-ticket-top">
                            <div
                                className="conf-logo"
                                style={{
                                    backgroundImage: `url('${info.logoUrl}')`,
                                }}
                            ></div>
                            <div>
                                <h2>{info.restaurantName}</h2>
                                <div className="addr">
                                    {info.restaurantAddress}
                                </div>
                            </div>
                        </div>
                        <div className="conf-perf"></div>
                        <div className="conf-stub">
                            <div>
                                <div className="k">
                                    {t('pages.confirmation.day')}
                                </div>
                                <div className="v">
                                    {info.start.format('MMM DD')}
                                </div>
                            </div>
                            <div>
                                <div className="k">
                                    {t('pages.confirmation.time')}
                                </div>
                                <div className="v">{`${info.start.format(
                                    'HH:mm',
                                )}-${info.end.format('HH:mm')}`}</div>
                            </div>
                            <div>
                                <div className="k">
                                    {t('pages.confirmation.guests')}
                                </div>
                                <div className="v">{info.guests}</div>
                            </div>
                        </div>
                    </div>

                    <div className="conf-actions">
                        <a
                            className="conf-link"
                            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                                `${info.restaurantName}, ${info.restaurantAddress}`,
                            )}`}
                            target="_blank"
                        >
                            <i className="material-icons">directions</i>
                            {t('pages.confirmation.getDirections')}
                        </a>
                        <a
                            className="conf-link"
                            href={info.restaurantWebsite}
                            target="_blank"
                        >
                            <i className="material-icons">language</i>
                            {t('pages.confirmation.checkTheWebsite')}
                        </a>
                    </div>

                    <div className="conf-note">
                        <h3>{t('pages.confirmation.noteFromRestaurant')}</h3>
                        <div
                            className="rest-text"
                            dangerouslySetInnerHTML={{
                                __html: normaliseHTMl(info.restaurantNote),
                            }}
                        />
                    </div>

                    <div
                        className="conf-map"
                        style={{
                            backgroundImage: `url('https://maps.googleapis.com/maps/api/staticmap?zoom=14&scale=2&size=1280x720&maptype=roadmap&markers=color:blue%7Clabel:${info.restaurantName.substr(
                                0,
                                1,
                            )}%7C${info.restaurantLat},${
                                info.restaurantLon
                            }&key=AIzaSyDTKaGPa0WkhsQGYTTa55NNVNL6YrPtTb0')`,
                        }}
                    ></div>

                    {showBadgeImageUrl && info.adImage && (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <a
                                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                target="_blank"
                                href={info.adImage.href}
                            >
                                <div
                                    style={{
                                        borderRadius: '16px',
                                        width: isMobile ? '100%' : '100%',
                                        marginTop: 32,
                                        aspectRatio: '16 / 10',
                                        backgroundPosition: 'right',
                                        backgroundSize: isMobile
                                            ? 'cover'
                                            : 'contain',
                                        backgroundRepeat: 'no-repeat',
                                        backgroundImage: `url('${info.adImage.imageUrl}')`,
                                    }}
                                ></div>
                            </a>
                        </div>
                    )}
                </div>
            </Style>
            <Footer />
        </>
    );
};

export default Confirmation;
